import React, { useContext, useRef } from "react";
import { useGSAP } from "@gsap/react";
import Context from "../OriginStoryProvider";
import { SplitText } from "gsap/SplitText";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";

const Header = () => {
	const {
		data: { content },
	} = useContext(Context);

	const headerContainer = useRef(null);
	const headlineQuoteText = useRef(null);
	const pinnedEl = useRef(null);

	useGSAP(
		() => {
			const split = new SplitText(headlineQuoteText.current, {
				type: "chars, words",
				wordsClass: "word",
			});

			// copied from https://gsap.com/community/forums/topic/27929-scrolltrigger-with-background-image-zoom-multiple-triggers/
			gsap.registerEffect({
				name: "zoom",
				effect: (targets, config) => {
					const vars = { transformOrigin: "0px 0px", ...config },
						{ scale, origin } = config,
						clamp = gsap.utils.clamp(-100 * (scale - 1), 0);
					delete vars.origin;
					vars.xPercent = clamp((0.5 - origin[0] * scale) * 100);
					vars.yPercent = clamp((0.5 - origin[1] * scale) * 100);
					vars.overwrite = "auto";
					return gsap.to(targets, vars);
				},
				extendTimeline: true,
				defaults: { origin: [0.5, 0.5], scale: 2 },
			});

			gsap.set(".chapter-2__header__pinned-el__bg-img", { xPercent: -50 });

			const tl = gsap
				.timeline()
				.to(".chapter-2__headline-quote--1__quote", {
					"--lquo-opacity": 1,
				})
				.from(split.chars, {
					opacity: 0,
					rotate: (i) => -i * 0.1,
					y: (i) => -i * 0.1,
					skewY: -5,
					stagger: 0.1,
				})
				.from(".chapter-2__headline-quote--1__citation", {
					opacity: 0,
				})
				.to(".chapter-2__header__pinned-el .main-grid__body", {
					y: "-100vh",
					delay: 1,
				})
				.to(
					".chapter-2__header__pinned-el__bg-color",
					{
						opacity: 0,
					},
					"<"
				)
				.to(".chapter-2__header__pinned-el__bg-img", {
					opacity: 1,
				})
				.zoom(".chapter-2__header__pinned-el__bg-img", {
					scale: 5,
					origin: [0.84, 0.28],
				})
				.from(".chapter-2__header__pinned-el__map-img", {
					scale: 0,
					delay: 0.5,
				})
				.to({}, { duration: 1 });

			ScrollTrigger.create({
				trigger: pinnedEl.current,
				start: "top top",
				end: () => "bottom+=400%",
				pin: true,
				scrub: 2,
				animation: tl,
			});
		},
		{ scope: headerContainer.current, dependencies: [content.chapters] }
	);

	return (
		<header className="chapter-2__header__container" ref={headerContainer}>
			<div className="chapter-2__header__wrapper main-grid">
				<div className="main-grid__margin"></div>
				<div className="main-grid__body">
					<div className="chapter-2__title-container">
						<p className="chapter-2__label label-text">
							{content.chapters.second.label}
						</p>
						<h2 className="chapter-2__title">
							{content.chapters.second.title}
						</h2>
					</div>
				</div>
				<div className="main-grid__margin"></div>
			</div>
			<div className="chapter-2__header__pinned-el" ref={pinnedEl}>
				<img
					className="chapter-2__header__pinned-el__bg-img"
					srcSet={`${mainScript.assetPath}images/webp/chapter-2/la-map-1887-400w.webp 400w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-600w.webp 600w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-800w.webp 800w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-1000w.webp 1000w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-1200w.webp 1200w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-1600w.webp 1600w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-2000w.webp 2000w, 
						${mainScript.assetPath}images/webp/chapter-2/la-map-1887-2400w.webp 2400w, 
						${mainScript.assetPath}images/jpg/chapter-2/la-map-1887.jpg 2880w`}
					sizes="(max-width: 400px) 600px, (max-width: 600px) 800px, (max-width: 800px) 1200px, (max-width: 1000px) 1600px, (max-width: 1200px) 1600px, (max-width: 1600px) 2000px, (min-width: 2000px) 2880px"
					src={`${mainScript.assetPath}images/jpg/chapter-2/la-map-1887.jpg`}
					loading="lazy"
					alt=""
					width="2880"
					height="2020"
				/>
				<div className="chapter-2__header__pinned-el__bg-color"></div>
				<img
					className="chapter-2__header__pinned-el__map-img"
					srcSet={`${mainScript.assetPath}images/webp/chapter-2/pomona-map-400w.webp 400w, ${mainScript.assetPath}images/webp/chapter-2/pomona-map-600w.webp 600w, ${mainScript.assetPath}images/webp/chapter-2/pomona-map-800w.webp 800w, ${mainScript.assetPath}images/webp/chapter-2/pomona-map-1000w.webp 1000w, ${mainScript.assetPath}images/webp/chapter-2/pomona-map-1200w.webp 1200w`}
					sizes="(max-width: 400px) 400px, (max-width: 600px) 600px, (max-width: 800px) 800px, (max-width: 1000px) 1000px, (min-width: 1001px) 1200px"
					src={`${mainScript.assetPath}images/jpg/chapter-2/pomona-map.jpg`}
					width="1600"
					height="1994"
					loading="lazy"
					alt="Map of Pomona, California from 1887"
				/>
				<div className="main-grid">
					<div className="main-grid__margin"></div>
					<div className="main-grid__body">
						<p
							className="chapter-2__headline-quote--1__quote headline-quote"
							ref={headlineQuoteText}
						>
							{content.chapters.second.header.quote}&rdquo;
						</p>
						<cite
							className="chapter-2__headline-quote--1__citation quote-attribute"
							dangerouslySetInnerHTML={{
								__html: content.chapters.second.header.attribution,
							}}
						></cite>
					</div>
					<div className="main-grid__margin"></div>
				</div>
				<div className="chapter-2__header__spacer"></div>
			</div>
			<p className="chapter-2__header__img-caption img-caption">
				{content.chapters.second.header.img_caption}
			</p>
		</header>
	);
};

export default Header;
